import Cookies from "js-cookie";
import localStorage from "local-storage-fallback";

import history from "src/helpers/browser-history";
import privateRoutes from "src/routes/private-routes";
import publicRoutes from "src/routes/public-routes";

import { chatWidgetLogout } from "@help-center/flip-chat-widget";
import NewUsersAPI from "src/api/new-interfaces/live/users";
import { getApiHeader, handleApiResponseError } from "src/api/request-manager/utils";
import { EXCLUDED_COMEBACK_ROUTES } from "src/consts/routes";
import { ERROR_CODE_INTERNATIONAL_TRANSACTION_RESTRICTED, ERROR_CODE_MONEY_TRANSFER_RESTRICTED } from "src/consts/user";
import { reset } from "src/helpers/amplitude/analytics-helpers";
import { loadUserRole, removeUserRole } from "src/helpers/role";
import { fetchUser } from "src/services/actions/user";
import { cleanRoute } from "src/services/helpers/store";
import store from "src/services/store";
import { clearLocalStorageValuesAfterLogout } from "../storage";

const tokenLabel = "bf-token";
const signupTokenLabel = "bf-signup-token";

window.ffbState = {};

export function loadToken() {
  const tokenData = localStorage.getItem(tokenLabel);
  if (!tokenData) return null;

  try {
    const parsed = JSON.parse(tokenData);
    return parsed.token || tokenData; // Fallback for old format
  } catch {
    return tokenData; // Fallback for old format
  }
}

export function saveToken(token) {
  const tokenData = {
    token,
    timestamp: Date.now()
  };
  return localStorage.setItem(tokenLabel, JSON.stringify(tokenData));
}

export function destroyToken() {
  localStorage.removeItem(tokenLabel);
}

export function forwardLogin(comebackPath) {
  history.push(comebackPath || privateRoutes.home.path);
}

export function forwardVerification(token) {
  saveToken(token);
  history.push(privateRoutes.verificationEmail.path);
}

export function removeSessionChatWidget() {
  chatWidgetLogout();
  window.ffbState.isAuthChatWidget = false;
}

export function refetchUserInfo(error) {
  const { response = {} } = error;
  const { location } = window;
  const isMTRestricted = !!response?.data?.errors?.find(({ code }) => code === ERROR_CODE_MONEY_TRANSFER_RESTRICTED);
  const isITRestricted = !!response?.data?.errors?.find(
    ({ code }) => code === ERROR_CODE_INTERNATIONAL_TRANSACTION_RESTRICTED
  );

  if (isMTRestricted && location.pathname !== privateRoutes.transaction.path) {
    store.dispatch(fetchUser(false));
    cleanRoute(true);
    if (location.pathname.includes(privateRoutes.approval.path)) {
      history.replace({ pathname: privateRoutes.approval.path, state: { isMTRestricted } });
    } else {
      history.replace(privateRoutes.transaction.path);
    }

    return handleApiResponseError(error);
  }

  if (isITRestricted && location.pathname !== privateRoutes.internationalTransaction.path) {
    store.dispatch(fetchUser(false));
    cleanRoute(true);
    history.replace(privateRoutes.internationalTransaction.path);
    return handleApiResponseError(error);
  }

  return handleApiResponseError(error);
}

export async function forwardLogout(checkComeback = false) {
  cleanRoute(true);

  let finalURL = publicRoutes.login.path;
  const searchParams = new URLSearchParams();

  const currentHeaders = getApiHeader();
  const previousURL = history.location.pathname;

  if (checkComeback && !EXCLUDED_COMEBACK_ROUTES.includes(previousURL)) {
    searchParams.append("comeback", previousURL + location.search);
  }

  if (!!searchParams.toString()) {
    finalURL = `${publicRoutes.login.path}?${searchParams.toString()}`;
  }

  reset();
  destroyToken();
  removeUserRole();
  removeSessionChatWidget();
  clearLocalStorageValuesAfterLogout();

  // if it's already in Login Page, we don't need to push it again
  // because in certain page, it will have many API request,
  // and will invoke "forwardLogout" many times if the token was expired
  // and it will remove the "comeback" query, so we need to push it once
  if (previousURL !== publicRoutes.login.path) {
    history.push(finalURL);
  }

  // if `checkComeback` is true, it's mostly because the token is already expired
  // therefor it doesn't need to hit the logoutUser API
  if (checkComeback) {
    window.location.reload();
    return;
  }

  const logout = NewUsersAPI.logoutUser;
  logout(currentHeaders).then(() => {
    window.location.reload();
  });
}

export function isAuthenticated() {
  return !!loadToken() && !!loadUserRole();
}

export function loadSignupToken() {
  return Cookies.get(signupTokenLabel);
}

export function removeSignupToken() {
  Cookies.remove(signupTokenLabel);
}

export function forwardToCreateId(token) {
  Cookies.set(signupTokenLabel, token, { expires: 1 });
  const { search } = history.location;
  history.push(`${publicRoutes.newUser.path}${search}`);
}

export function checkUnauthorizedResponse(response) {
  if (response.status === 401) forwardLogout();
}

export function forwardMaintenance() {
  if (window.location.pathname !== publicRoutes.maintenance.path) {
    history.push(publicRoutes.maintenance.path);
  }
}
